import React from "react"

import { faClock } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import moment from "moment-timezone"

import { PayerStatus } from "@common-shared/config/oneVerify/oneVerifyPayerStatus.config"
import type {
    IOneVerifyPayerStatusIncident,
    IOneVerifyPayerStatusPayer,
} from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { DrawerElement } from "@front-shared/elements/drawer.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import { PAYER_STATUS_CONFIG } from "../../config/payerStatus.config"
import { PayerStatusHelper } from "../../helpers/payerStatus.helper"
import { UtilHelper } from "../../helpers/util.helper"
import { PayerStatusBadgeComponent } from "./payerStatusBadge.component"

export const PayerStatusDrawerComponent: React.FunctionComponent<
    IPayerStatusDrawerComponentProps
> = props => {
    const operationalConfig =
        PAYER_STATUS_CONFIG.statuses[PayerStatus.OPERATIONAL]

    const issueConfig = PAYER_STATUS_CONFIG.statuses[PayerStatus.ISSUE_DETECTED]

    const noHistoryLabel = `No issues in the last ${PAYER_STATUS_CONFIG.historyPeriodDays} days`

    // Since when payer has its current status, unknown when issue started before history
    const getStatusSinceLabel = (): string | null => {
        if (props.payer?.status === PayerStatus.ISSUE_DETECTED) {
            const ongoingIncident = props.incidents.find(
                incident => !incident.recoveredAt,
            )

            return ongoingIncident
                ? `Status since ${PayerStatusHelper.getRelativeTimeLabel(ongoingIncident.startedAt)}`
                : null
        }

        const lastRecoveredAt = props.incidents.find(
            incident => !!incident.recoveredAt,
        )?.recoveredAt

        return lastRecoveredAt
            ? `Status since ${PayerStatusHelper.getRelativeTimeLabel(lastRecoveredAt)}`
            : noHistoryLabel
    }

    const renderIncident = (
        incident: IOneVerifyPayerStatusIncident,
    ): React.ReactNode => {
        const durationLabel = PayerStatusHelper.getIncidentDurationLabel(
            incident,
            props.fetchedAt,
        )

        return (
            <li
                key={incident.id}
                className="flex gap-[12px] border-0 border-b border-solid border-brand-warmLight2 py-[12px]"
            >
                <FontAwesomeIcon
                    icon={issueConfig.icon}
                    className="mt-[4px]"
                    style={{ color: issueConfig.color }}
                    aria-hidden="true"
                />

                <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
                    <div className="flex flex-wrap justify-between gap-[8px]">
                        <PrimaryText typography="textSemibold">
                            {issueConfig.label}
                        </PrimaryText>

                        <PrimaryText
                            typography="textSmall"
                            className="opacity-60"
                        >
                            {incident.recoveredAt
                                ? moment(incident.startedAt).format(
                                      PAYER_STATUS_CONFIG.dayTimeFormat,
                                  )
                                : PayerStatusHelper.getRelativeTimeLabel(
                                      incident.startedAt,
                                  )}
                        </PrimaryText>
                    </div>

                    <div className="flex items-center gap-[6px]">
                        <FontAwesomeIcon
                            icon={
                                incident.recoveredAt
                                    ? operationalConfig.icon
                                    : faClock
                            }
                            className="text-[12px]"
                            style={{
                                color: incident.recoveredAt
                                    ? operationalConfig.color
                                    : NIRVANA_COLORS.brand.grey7,
                            }}
                        />

                        <PrimaryText typography="textSmall">
                            {incident.recoveredAt
                                ? `Resolved ${PayerStatusHelper.getIncidentEndLabel(incident)} · ${durationLabel}`
                                : `Ongoing · ${durationLabel} so far`}
                        </PrimaryText>
                    </div>
                </div>
            </li>
        )
    }

    const statusSinceLabel = getStatusSinceLabel()

    return (
        <DrawerElement
            isOpen={props.isOpen}
            onClose={props.onClose}
            width={440}
            title={
                props.payer ? (
                    <>
                        <PrimaryText typography="h5">
                            {PayerStatusHelper.getPayerName(props.payer)}
                        </PrimaryText>

                        <PrimaryText
                            typography="textSmall"
                            className="opacity-60"
                        >
                            {`Payer ID ${props.payer.payerId}`}
                        </PrimaryText>
                    </>
                ) : null
            }
            footer={
                <div className="flex flex-wrap items-center justify-between gap-[12px]">
                    <PrimaryText className="opacity-60">
                        Still having trouble?
                    </PrimaryText>

                    <ButtonElement
                        htmlType="button"
                        type="default"
                        size="middle"
                        label="Contact support"
                        onClick={UtilHelper.openSupportEmail}
                    />
                </div>
            }
        >
            {props.payer ? (
                <div className="flex flex-col gap-[24px]">
                    <div className="flex flex-col gap-[12px]">
                        <div className="flex flex-wrap items-center gap-[10px]">
                            <PayerStatusBadgeComponent
                                status={props.payer.status}
                            />

                            {statusSinceLabel ? (
                                <PrimaryText
                                    typography="textSmall"
                                    className="opacity-60"
                                >
                                    {statusSinceLabel}
                                </PrimaryText>
                            ) : null}
                        </div>

                        <PrimaryText className="rounded-[14px] border border-solid border-brand-warmLight2 bg-brand-offWhite px-[16px] py-[14px]">
                            {
                                PAYER_STATUS_CONFIG.statuses[props.payer.status]
                                    .description
                            }
                        </PrimaryText>
                    </div>

                    <div>
                        <PrimaryText
                            typography="textSemibold"
                            className="mb-[8px]"
                        >
                            {`Last ${PAYER_STATUS_CONFIG.historyPeriodDays} days`}
                        </PrimaryText>

                        {props.incidents.length ? (
                            <ol className="!m-0 list-none p-0">
                                {props.incidents.map(renderIncident)}
                            </ol>
                        ) : (
                            <PrimaryText className="opacity-60">
                                {`${noHistoryLabel}.`}
                            </PrimaryText>
                        )}
                    </div>
                </div>
            ) : null}
        </DrawerElement>
    )
}

interface IPayerStatusDrawerComponentProps {
    isOpen: boolean
    payer: IOneVerifyPayerStatusPayer | null
    incidents: IOneVerifyPayerStatusIncident[] // of the payer, ongoing first, then newest
    fetchedAt: number
    onClose: () => void
}
